import { useState, useEffect, useRef } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { useNavigate } from "react-router-dom";

const HAWKINS_STATES = [
  { level: 20, name: "Shame", band: 1, color: "#8B0000" },
  { level: 30, name: "Guilt", band: 1, color: "#A52A2A" },
  { level: 50, name: "Apathy", band: 2, color: "#CD5C5C" },
  { level: 75, name: "Grief", band: 2, color: "#F08080" },
  { level: 100, name: "Fear", band: 3, color: "#FFA500" },
  { level: 125, name: "Desire", band: 4, color: "#FFD700" },
  { level: 150, name: "Anger", band: 4, color: "#ADFF2F" },
  { level: 175, name: "Pride", band: 4, color: "#7FFF00" },
  { level: 200, name: "Courage", band: 5, color: "#00FF7F" },
  { level: 250, name: "Neutrality", band: 5, color: "#00FA9A" },
  { level: 310, name: "Willingness", band: 5, color: "#40E0D0" },
  { level: 350, name: "Acceptance", band: 6, color: "#00CED1" },
  { level: 400, name: "Reason", band: 6, color: "#4169E1" },
  { level: 500, name: "Love", band: 6, color: "#9370DB" },
  { level: 540, name: "Joy", band: 7, color: "#BA55D3" },
  { level: 600, name: "Peace", band: 7, color: "#DA70D6" },
  { level: 700, name: "Enlightenment", band: 7, color: "#EE82EE" }
];

const BIOFIELD_BANDS = {
  1: {
    name: "Stabilize",
    intent: "Grounding & Safety",
    states: ["Shame", "Guilt"],
    range: "20-50",
    tempo: [40, 55],
    key: "C",
    description: "Deep grounding for collapse states. Slow heartbeat rhythm, warm safety.",
    color: "#8B0000",
    texture: "Soft drones, perfect fifths, minimal movement"
  },
  2: {
    name: "Ignite Spark",
    intent: "Gentle Activation",
    states: ["Apathy", "Grief"],
    range: "50-75",
    tempo: [55, 70],
    key: "A minor",
    description: "Awakening life force. Permission to feel, gentle hope emerges.",
    color: "#CD5C5C",
    texture: "Minor to suspended shifts, soft piano"
  },
  3: {
    name: "Regulate",
    intent: "Nervous System Calm",
    states: ["Fear"],
    range: "100",
    tempo: [60, 72],
    key: "D",
    description: "Predictable rhythms calm hypervigilance. Extended exhale pacing.",
    color: "#FFA500",
    texture: "Mixolydian mode, controlled shimmer"
  },
  4: {
    name: "Channel Drive",
    intent: "Direct Energy",
    states: ["Desire", "Anger", "Pride"],
    range: "125-175",
    tempo: [80, 105],
    key: "E minor",
    description: "Transform agitation into focused power. Strong groove, clear direction.",
    color: "#FFD700",
    texture: "Powerful bass, 5→1 cadences"
  },
  5: {
    name: "Lock Coherence",
    intent: "Functional Flow",
    states: ["Courage", "Neutrality", "Willingness"],
    range: "200-310",
    tempo: [90, 115],
    key: "G major",
    description: "Sustained okayness. Forward momentum, clear choices available.",
    color: "#00FF7F",
    texture: "Pentatonic, bright stereo movement"
  },
  6: {
    name: "Expand Field",
    intent: "Integration & Warmth",
    states: ["Acceptance", "Reason", "Love"],
    range: "350-500",
    tempo: [60, 90],
    key: "C major",
    description: "Mental clarity meets heart. Lush harmony, presence affects others.",
    color: "#4169E1",
    texture: "Extended chords, wide reverb"
  },
  7: {
    name: "Dissolve",
    intent: "Spacious Being",
    states: ["Joy", "Peace", "Enlightenment"],
    range: "540-1000",
    tempo: [0, 60],
    key: "Universal",
    description: "Non-personal joy. Dissolve into vastness, overtone-rich space.",
    color: "#BA55D3",
    texture: "Drones, bells, long reverb tails"
  }
};

export default function ResonanceShifter() {
  const navigate = useNavigate();
  const [currentState, setCurrentState] = useState(250); // Default: Neutrality
  const [targetBand, setTargetBand] = useState("auto");
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [currentBand, setCurrentBand] = useState(null);
  const [history, setHistory] = useState([]);
  const [showProgress, setShowProgress] = useState(false);
  
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const gainNodeRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Load history from localStorage
    const saved = localStorage.getItem("resonance_history");
    if (saved) {
      try {
        setHistory(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to load history:", e);
      }
    }
  }, []);

  const getCurrentStateInfo = () => {
    return HAWKINS_STATES.find(s => s.level === currentState) || HAWKINS_STATES[0];
  };

  const getBandForState = (level) => {
    const state = HAWKINS_STATES.find(s => s.level === level);
    return state ? state.band : 5;
  };

  const getTargetBandForSession = () => {
    if (targetBand !== "auto") return parseInt(targetBand);
    
    const currentBandNum = getBandForState(currentState);
    // Auto: move up one band if possible
    return Math.min(currentBandNum + 1, 7);
  };

  const startSession = async () => {
    const stateInfo = getCurrentStateInfo();
    const fromBand = stateInfo.band;
    const toBand = getTargetBandForSession();
    const bandInfo = BIOFIELD_BANDS[toBand];

    // Initialize Web Audio
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const ctx = audioContextRef.current;
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    // Create gain node for volume control
    gainNodeRef.current = ctx.createGain();
    gainNodeRef.current.gain.value = 0.15;
    gainNodeRef.current.connect(ctx.destination);

    // Generate resonance tones based on band recipe
    generateResonanceTones(ctx, bandInfo, gainNodeRef.current);

    setSessionActive(true);
    setCurrentBand(toBand);
    setSessionTime(0);

    // Record session start
    const sessionRecord = {
      timestamp: new Date().toISOString(),
      fromState: stateInfo.name,
      fromLevel: stateInfo.level,
      fromBand,
      toBand,
      intent: bandInfo.intent
    };
    
    const newHistory = [...history, sessionRecord];
    setHistory(newHistory);
    localStorage.setItem("resonance_history", JSON.stringify(newHistory.slice(-20))); // Keep last 20

    // Start session timer
    timerRef.current = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    toast.success(`Resonance Session Active: ${bandInfo.name}`, {
      description: `${stateInfo.name} → ${bandInfo.intent}`
    });
  };

  const generateResonanceTones = (ctx, bandInfo, gainNode) => {
    const baseFreq = 432; // Always 432Hz tuned
    const [minTempo, maxTempo] = bandInfo.tempo;
    const tempo = (minTempo + maxTempo) / 2;
    
    // Create carrier tone (root frequency)
    const carrier = ctx.createOscillator();
    carrier.type = bandInfo.name === "Dissolve" ? "sine" : "triangle";
    carrier.frequency.value = baseFreq;
    
    const carrierGain = ctx.createGain();
    carrierGain.gain.value = 0.3;
    carrier.connect(carrierGain);
    carrierGain.connect(gainNode);
    carrier.start();
    
    // Create binaural beat based on tempo
    const beatFreq = tempo / 60; // Convert BPM to Hz
    const binaural = ctx.createOscillator();
    binaural.type = "sine";
    binaural.frequency.value = baseFreq + beatFreq;
    
    const binauralGain = ctx.createGain();
    binauralGain.gain.value = 0.2;
    binaural.connect(binauralGain);
    binauralGain.connect(gainNode);
    binaural.start();
    
    // Create harmonic (perfect fifth)
    const harmonic = ctx.createOscillator();
    harmonic.type = "sine";
    harmonic.frequency.value = baseFreq * 1.5; // Perfect fifth
    
    const harmonicGain = ctx.createGain();
    harmonicGain.gain.value = 0.15;
    harmonic.connect(harmonicGain);
    harmonicGain.connect(gainNode);
    harmonic.start();

    // Store oscillators for cleanup
    oscillatorsRef.current = [carrier, binaural, harmonic];
  };

  const stopSession = () => {
    // Stop all oscillators
    oscillatorsRef.current.forEach(osc => {
      try {
        osc.stop();
      } catch (e) {
        // Already stopped
      }
    });
    oscillatorsRef.current = [];

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setSessionActive(false);
    setCurrentBand(null);
    
    toast.info("Resonance session completed", {
      description: `Duration: ${Math.floor(sessionTime / 60)}m ${sessionTime % 60}s`
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRecommendation = () => {
    if (history.length < 3) return null;
    
    const recentSessions = history.slice(-5);
    const avgFromLevel = recentSessions.reduce((sum, s) => sum + s.fromLevel, 0) / recentSessions.length;
    
    if (avgFromLevel > currentState + 50) {
      return "Your recent sessions suggest higher baseline. Consider selecting a higher state.";
    }
    return null;
  };

  const stateInfo = getCurrentStateInfo();
  const activeBandInfo = currentBand ? BIOFIELD_BANDS[currentBand] : null;
  const recommendation = getRecommendation();

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#a4ff2d]">
            BIOFIELD STATE SHIFTER
          </h1>
          <p className="text-sm text-muted-foreground">
            Intensity Scale-based quantum sound experience · 432Hz tuned
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/tools')}>
          Back to Tools
        </Button>
      </div>

      {/* Active Session Display */}
      {sessionActive && activeBandInfo && (
        <Card className="bg-gradient-to-r from-[#ff1744]/10 to-[#a4ff2d]/10 border-[#a4ff2d]">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="text-2xl font-bold" style={{ color: activeBandInfo.color }}>
                  Band {currentBand}: {activeBandInfo.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {activeBandInfo.intent} · {activeBandInfo.texture}
                </div>
                <div className="text-lg font-mono text-[#a4ff2d]">
                  {formatTime(sessionTime)}
                </div>
              </div>
              <Button variant="destructive" onClick={stopSession} size="lg">
                End Session
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current State Selection */}
        <Card className="sg-frame">
          <CardHeader>
            <CardTitle>1. Current State</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="text-center space-y-2">
                <div 
                  className="text-4xl font-bold"
                  style={{ color: stateInfo.color }}
                >
                  {stateInfo.name}
                </div>
                <div className="text-2xl text-muted-foreground">
                  Level {stateInfo.level}
                </div>
                <div className="text-sm text-[#a4ff2d]">
                  Band {stateInfo.band}: {BIOFIELD_BANDS[stateInfo.band].name}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select your current state:</Label>
                <Select 
                  value={currentState.toString()} 
                  onValueChange={(v) => setCurrentState(parseInt(v))}
                  disabled={sessionActive}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HAWKINS_STATES.map(state => (
                      <SelectItem key={state.level} value={state.level.toString()}>
                        {state.name} ({state.level}) - Band {state.band}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Or use slider:</Label>
                <Slider
                  value={[currentState]}
                  onValueChange={(v) => setCurrentState(v[0])}
                  min={20}
                  max={700}
                  step={10}
                  disabled={sessionActive}
                  className="my-4"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Target Band & Session */}
        <Card className="sg-frame">
          <CardHeader>
            <CardTitle>2. Session Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label>Target Band:</Label>
                <Select value={targetBand} onValueChange={setTargetBand} disabled={sessionActive}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (Next Level Up)</SelectItem>
                    {Object.entries(BIOFIELD_BANDS).map(([num, band]) => (
                      <SelectItem key={num} value={num}>
                        Band {num}: {band.name} ({band.range})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {recommendation && (
                <div className="p-3 bg-[#a4ff2d]/10 border border-[#a4ff2d]/30 rounded text-sm">
                  💡 {recommendation}
                </div>
              )}

              <div className="p-4 bg-black/40 rounded space-y-2">
                <div className="text-sm font-semibold text-[#a4ff2d]">
                  Session Preview:
                </div>
                {!sessionActive && (
                  <>
                    <div className="text-sm">
                      From: <span style={{ color: stateInfo.color }}>{stateInfo.name}</span> (Band {stateInfo.band})
                    </div>
                    <div className="text-sm">
                      To: Band {getTargetBandForSession()} - {BIOFIELD_BANDS[getTargetBandForSession()].name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      {BIOFIELD_BANDS[getTargetBandForSession()].description}
                    </div>
                  </>
                )}
              </div>

              {!sessionActive ? (
                <Button 
                  onClick={startSession}
                  className="w-full h-12 bg-gradient-to-r from-[#ff1744] to-[#a4ff2d]"
                  size="lg"
                >
                  Start Resonance Session
                </Button>
              ) : (
                <div className="text-center text-sm text-muted-foreground">
                  Session in progress...
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resonance Band Reference */}
      <Card className="sg-frame">
        <CardHeader>
          <CardTitle>7 Resonance Bands - Sound Recipes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries(BIOFIELD_BANDS).map(([num, band]) => (
              <div 
                key={num}
                className="p-4 border rounded hover:border-[#a4ff2d]/50 transition-colors"
                style={{ borderColor: `${band.color}40` }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div 
                        className="text-xl font-bold"
                        style={{ color: band.color }}
                      >
                        Band {num}: {band.name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        Level {band.range}
                      </div>
                    </div>
                    <div className="text-sm mb-2">
                      <span className="text-[#a4ff2d] font-semibold">States:</span>{' '}
                      {band.states.join(', ')}
                    </div>
                    <div className="text-sm mb-2">
                      <span className="text-[#a4ff2d] font-semibold">Intent:</span>{' '}
                      {band.intent}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {band.description}
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      {band.texture} · {band.tempo[0]}-{band.tempo[1]} BPM · Key: {band.key}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Session History & Progress */}
      <Card className="sg-frame">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Your Journey</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowProgress(!showProgress)}
          >
            {showProgress ? 'Hide' : 'Show'} Progress
          </Button>
        </CardHeader>
        {showProgress && (
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No sessions yet. Start your first resonance session above.
              </div>
            ) : (
              <div className="space-y-2">
                {history.slice(-10).reverse().map((session, idx) => (
                  <div 
                    key={idx}
                    className="p-3 bg-black/40 rounded text-sm flex items-center justify-between"
                  >
                    <div>
                      <div>
                        {session.fromState} (L{session.fromLevel}) → Band {session.toBand}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(session.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-xs text-[#a4ff2d]">
                      {session.intent}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Integration Links */}
      <Card className="sg-frame border-[#a4ff2d]/30">
        <CardHeader>
          <CardTitle>Enhance Your Session</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Button 
              variant="outline"
              className="h-auto py-4 flex flex-col items-start"
              onClick={() => navigate('/')}
            >
              <div className="font-semibold mb-1">432Hz Tone Generator</div>
              <div className="text-xs text-muted-foreground">
                Combine with multi-tone layers
              </div>
            </Button>
            <Button 
              variant="outline"
              className="h-auto py-4 flex flex-col items-start"
              onClick={() => {
                // Open quantum modal
                toast.info("Navigate to Home and open the Resonance panel");
              }}
            >
              <div className="font-semibold mb-1">Resonance Field</div>
              <div className="text-xs text-muted-foreground">
                Amplify resonance harmonization
              </div>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
